// One shape feeds the marker bar, the points list, and the transcript
// highlights, whichever surface owns the video: investigation leads and
// events, or case events and footage-check findings.
import { InvestigationLead } from "../../../db/schema";

export type VideoPointSource = "lead" | "event" | "finding";

export type VideoPoint = {
  id: string;
  tStartMs: number;
  tEndMs: number;
  kind: string; // VideoEventKind | lead category | finding kind
  severity?: number; // 1-5 for leads and findings
  title: string;
  description: string;
  explanation?: string;
  frameTs: number[];
  source: VideoPointSource;
  status?: string; // lead status
  lead?: InvestigationLead;
  confidence?: number;
  textRead?: string;
};

// Colors by what happened. Literal class strings so Tailwind keeps them.
export const VIDEO_KIND_STYLES: Record<string, { label: string; dot: string; ring: string; text: string; tint: string }> = {
  use_of_force: { label: "Use of force", dot: "bg-red-600", ring: "ring-red-300 dark:ring-red-800", text: "text-red-700 dark:text-red-400", tint: "bg-red-50 dark:bg-red-950/30" },
  resistance: { label: "Resistance", dot: "bg-orange-500", ring: "ring-orange-300 dark:ring-orange-800", text: "text-orange-700 dark:text-orange-400", tint: "bg-orange-50 dark:bg-orange-950/30" },
  weapon: { label: "Weapon", dot: "bg-red-800", ring: "ring-red-400 dark:ring-red-900", text: "text-red-800 dark:text-red-400", tint: "bg-red-50 dark:bg-red-950/30" },
  command: { label: "Command", dot: "bg-amber-500", ring: "ring-amber-300 dark:ring-amber-800", text: "text-amber-700 dark:text-amber-400", tint: "bg-amber-50 dark:bg-amber-950/30" },
  compliance: { label: "Compliance", dot: "bg-lime-500", ring: "ring-lime-300 dark:ring-lime-800", text: "text-lime-700 dark:text-lime-400", tint: "bg-lime-50 dark:bg-lime-950/30" },
  contact: { label: "Contact", dot: "bg-blue-500", ring: "ring-blue-300 dark:ring-blue-800", text: "text-blue-600 dark:text-blue-300", tint: "bg-blue-50 dark:bg-blue-900/40" },
  arrival: { label: "Arrival", dot: "bg-sky-500", ring: "ring-sky-300 dark:ring-sky-800", text: "text-sky-700 dark:text-sky-300", tint: "bg-sky-50 dark:bg-sky-950/30" },
  search: { label: "Search", dot: "bg-purple-500", ring: "ring-purple-300 dark:ring-purple-800", text: "text-purple-700 dark:text-purple-300", tint: "bg-purple-50 dark:bg-purple-950/30" },
  seizure: { label: "Seizure", dot: "bg-violet-600", ring: "ring-violet-300 dark:ring-violet-800", text: "text-violet-700 dark:text-violet-300", tint: "bg-violet-50 dark:bg-violet-950/30" },
  handcuffing: { label: "Handcuffing", dot: "bg-slate-500", ring: "ring-slate-300 dark:ring-slate-700", text: "text-slate-700 dark:text-slate-300", tint: "bg-slate-50 dark:bg-slate-900/40" },
  injury: { label: "Injury", dot: "bg-pink-600", ring: "ring-pink-300 dark:ring-pink-800", text: "text-pink-700 dark:text-pink-400", tint: "bg-pink-50 dark:bg-pink-950/30" },
  medical: { label: "Medical", dot: "bg-pink-500", ring: "ring-pink-300 dark:ring-pink-800", text: "text-pink-700 dark:text-pink-400", tint: "bg-pink-50 dark:bg-pink-950/30" },
  vehicle: { label: "Vehicle", dot: "bg-teal-500", ring: "ring-teal-300 dark:ring-teal-800", text: "text-teal-700 dark:text-teal-300", tint: "bg-teal-50 dark:bg-teal-950/30" },
  property: { label: "Property", dot: "bg-cyan-600", ring: "ring-cyan-300 dark:ring-cyan-800", text: "text-cyan-700 dark:text-cyan-300", tint: "bg-cyan-50 dark:bg-cyan-950/30" },
  text_read: { label: "Text read", dot: "bg-indigo-500", ring: "ring-indigo-300 dark:ring-indigo-800", text: "text-indigo-700 dark:text-indigo-300", tint: "bg-indigo-50 dark:bg-indigo-950/30" },
  other: { label: "Other", dot: "bg-gray-400", ring: "ring-gray-300 dark:ring-gray-600", text: "text-base-content/70", tint: "bg-base-200" },
  // Investigation lead categories.
  violence_threat: { label: "Violence / threat", dot: "bg-red-600", ring: "ring-red-300 dark:ring-red-800", text: "text-red-700 dark:text-red-400", tint: "bg-red-50 dark:bg-red-950/30" },
  witness_tampering: { label: "Witness tampering", dot: "bg-orange-600", ring: "ring-orange-300 dark:ring-orange-800", text: "text-orange-700 dark:text-orange-400", tint: "bg-orange-50 dark:bg-orange-950/30" },
  third_party_call: { label: "Third-party call", dot: "bg-amber-600", ring: "ring-amber-300 dark:ring-amber-800", text: "text-amber-700 dark:text-amber-400", tint: "bg-amber-50 dark:bg-amber-950/30" },
  drug_transaction: { label: "Drug transaction", dot: "bg-purple-600", ring: "ring-purple-300 dark:ring-purple-800", text: "text-purple-700 dark:text-purple-300", tint: "bg-purple-50 dark:bg-purple-950/30" },
  contraband: { label: "Contraband", dot: "bg-fuchsia-600", ring: "ring-fuchsia-300 dark:ring-fuchsia-800", text: "text-fuchsia-700 dark:text-fuchsia-300", tint: "bg-fuchsia-50 dark:bg-fuchsia-950/30" },
  money_movement: { label: "Money movement", dot: "bg-emerald-600", ring: "ring-emerald-300 dark:ring-emerald-800", text: "text-emerald-700 dark:text-emerald-300", tint: "bg-emerald-50 dark:bg-emerald-950/30" },
  coded_language: { label: "Coded language", dot: "bg-indigo-600", ring: "ring-indigo-300 dark:ring-indigo-800", text: "text-indigo-700 dark:text-indigo-300", tint: "bg-indigo-50 dark:bg-indigo-950/30" },
  admission: { label: "Admission", dot: "bg-rose-600", ring: "ring-rose-300 dark:ring-rose-800", text: "text-rose-700 dark:text-rose-400", tint: "bg-rose-50 dark:bg-rose-950/30" },
  flight_risk: { label: "Flight risk", dot: "bg-yellow-500", ring: "ring-yellow-300 dark:ring-yellow-800", text: "text-yellow-700 dark:text-yellow-400", tint: "bg-yellow-50 dark:bg-yellow-950/30" },
  discrepancy: { label: "Discrepancy", dot: "bg-purple-700", ring: "ring-purple-300 dark:ring-purple-800", text: "text-purple-700 dark:text-purple-300", tint: "bg-purple-50 dark:bg-purple-950/30" },
  report_note: { label: "New information", dot: "bg-blue-600", ring: "ring-blue-300 dark:ring-blue-800", text: "text-blue-600 dark:text-blue-300", tint: "bg-blue-50 dark:bg-blue-900/40" },
  // Footage-check findings (cases).
  unsupported_claim: { label: "Not supported by footage", dot: "bg-amber-600", ring: "ring-amber-300 dark:ring-amber-800", text: "text-amber-700 dark:text-amber-400", tint: "bg-amber-50 dark:bg-amber-950/30" },
  omitted_event: { label: "Not in the report", dot: "bg-blue-600", ring: "ring-blue-300 dark:ring-blue-800", text: "text-blue-600 dark:text-blue-300", tint: "bg-blue-50 dark:bg-blue-900/40" },
  timing_conflict: { label: "Timing differs", dot: "bg-orange-600", ring: "ring-orange-300 dark:ring-orange-800", text: "text-orange-700 dark:text-orange-400", tint: "bg-orange-50 dark:bg-orange-950/30" },
  contradiction: { label: "Contradicted by footage", dot: "bg-red-700", ring: "ring-red-300 dark:ring-red-800", text: "text-red-700 dark:text-red-400", tint: "bg-red-50 dark:bg-red-950/30" },
};

export const kindStyle = (kind: string) => VIDEO_KIND_STYLES[kind] || VIDEO_KIND_STYLES.other;
export const kindLabel = (kind: string) => kindStyle(kind).label || kind.replace(/_/g, " ");

export function formatClock(ms: number | null | undefined): string {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

// Points whose span contains the playhead (open end so a point that starts
// exactly where another ends is not double-lit).
export function pointsAt(points: VideoPoint[], ms: number): VideoPoint[] {
  return points.filter((p) => ms >= p.tStartMs && ms < Math.max(p.tEndMs, p.tStartMs + 1000));
}

export function nextPoint(points: VideoPoint[], ms: number): VideoPoint | null {
  const sorted = [...points].sort((a, b) => a.tStartMs - b.tStartMs);
  return sorted.find((p) => p.tStartMs > ms + 500) || null;
}
export function prevPoint(points: VideoPoint[], ms: number): VideoPoint | null {
  const sorted = [...points].sort((a, b) => b.tStartMs - a.tStartMs);
  return sorted.find((p) => p.tStartMs < ms - 1500) || null;
}

// Investigation leads with a video anchor, mapped to points.
export function leadToPoint(l: InvestigationLead): VideoPoint | null {
  const a = l.video_anchor as { t_start_ms: number; t_end_ms: number; frame_ts: number[]; kind?: string } | null;
  const start = a?.t_start_ms ?? l.start_ms;
  if (start == null) return null;
  return {
    id: `lead:${l.id}`,
    tStartMs: start,
    tEndMs: a?.t_end_ms ?? l.end_ms ?? start,
    kind: l.category,
    severity: l.severity,
    title: kindLabel(l.category),
    description: l.quote_translated || l.quote,
    explanation: l.explanation || undefined,
    frameTs: a?.frame_ts || [Math.round(start / 1000)],
    source: "lead",
    status: l.status,
    lead: l,
    confidence: l.confidence != null ? Number(l.confidence) : undefined,
  };
}

export type VideoEventLike = {
  id: string;
  t_start_ms: number;
  t_end_ms: number;
  kind: string;
  description: string;
  actors?: { label: string }[];
  confidence: number;
  frame_ts: number[];
  text_read?: string;
};

export function eventToPoint(e: VideoEventLike): VideoPoint {
  return {
    id: `event:${e.id}`,
    tStartMs: e.t_start_ms,
    tEndMs: e.t_end_ms,
    kind: e.kind,
    title: kindLabel(e.kind),
    description: e.description,
    explanation: e.actors?.length ? e.actors.map((a) => a.label).join(", ") : undefined,
    frameTs: e.frame_ts || [],
    source: "event",
    confidence: e.confidence,
    textRead: e.text_read,
  };
}

// Events that a lead already covers (same span) are dropped so the bar
// does not show two dots for one moment.
export function mergePoints(leads: VideoPoint[], events: VideoPoint[]): VideoPoint[] {
  const covered = (e: VideoPoint) => leads.some((l) => Math.abs(l.tStartMs - e.tStartMs) <= 3000 && l.kind !== "other");
  return [...leads, ...events.filter((e) => !covered(e))].sort((a, b) => a.tStartMs - b.tStartMs);
}
